import { useRef } from 'react';
import { Bug } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useOrganization } from '@/contexts/OrganizationContext';
import { getConfiguredFrontChatId } from '@/lib/frontChat';
import {
  useManagedFloatingWidgetPlacement,
  type ManagedFloatingWidgetId,
} from '@/lib/floatingWidgetLayout';
import {
  MANAGED_FLOATING_WIDGET_ATTRIBUTE,
  useFloatingWidgetSuppression,
} from '@/lib/floatingWidgetSuppression';
import {
  downloadDebugPayload,
  isNonProductionDebugEnvironment,
} from '@/lib/debugClipboard';
import { useWidgetPreferences } from '@/lib/widgetPreferences';
import { toast } from '@/hooks/use-toast';

interface DebugClipboardButtonProps {
  companionWidgetIds?: ManagedFloatingWidgetId[];
}

export function DebugClipboardButton({ companionWidgetIds = ['agent-fab'] }: DebugClipboardButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const context = useOrganization();
  const location = useLocation();
  const { preferences } = useWidgetPreferences();
  const hasSupportChatLauncher = preferences.front && Boolean(getConfiguredFrontChatId());
  const activeCompanionWidgetIds = hasSupportChatLauncher
    ? Array.from(new Set<ManagedFloatingWidgetId>(['front-chat', ...companionWidgetIds]))
    : companionWidgetIds;
  const placement = useManagedFloatingWidgetPlacement('debug-clipboard', [
    ...activeCompanionWidgetIds,
    'debug-clipboard',
  ]);
  const enabled = isNonProductionDebugEnvironment();
  useFloatingWidgetSuppression(buttonRef, { enabled, suppressWhenDialogOpen: false });

  if (!enabled) {
    return null;
  }

  const handleDownloadDebugData = async () => {
    try {
      downloadDebugPayload(context);
      toast({
        title: 'Debug data downloaded',
        description: 'JSON context was saved as a file.',
      });
    } catch (error) {
      console.error('Failed to download debug data', error);
      toast({
        title: 'Could not download debug data',
        description: error instanceof Error ? error.message : 'File download failed.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Download debug data"
      title="Download debug data"
      className="pointer-events-auto fixed z-[2147483647] h-10 w-10 rounded-full bg-orange-500 text-white shadow-lg transition-transform hover:scale-105 hover:bg-orange-600 hover:text-white focus-visible:ring-2 focus-visible:ring-orange-500"
      style={{
        right: `${placement.right}px`,
        bottom: `${placement.bottom}px`,
      }}
      data-debug-path={`${location.pathname}${location.search}${location.hash}`}
      {...{ [MANAGED_FLOATING_WIDGET_ATTRIBUTE]: 'debug-clipboard' }}
      onClick={() => void handleDownloadDebugData()}
    >
      <Bug className="h-5 w-5" />
    </Button>
  );
}
